/**
 * Action Points Service
 *
 * Handles database operations related to action points regeneration and tracking.
 * Used by the tick processor to regenerate AP at the start of each tick.
 */

import { supabase } from '@/lib/supabase'
import {
  calculateActionPointsForTick,
  calculateAverageOrganizationStrength,
} from '@/lib/actionPoints'

// Query key factory for action points queries
export const actionPointKeys = {
  all: ['actionPoints'] as const,
  byParty: (partyId: string) => ['actionPoints', 'party', partyId] as const,
  byPartyTick: (partyId: string, tick: number) =>
    ['actionPoints', 'party', partyId, 'tick', tick] as const,
}

/**
 * Regenerate action points for a party at the start of a tick.
 *
 * This function:
 * 1. Fetches the party's current money
 * 2. Fetches all party district states to calculate average organization strength
 * 3. Calculates new AP total using the formula
 * 4. Updates the party's action_points field
 *
 * @param partyId - The party to regenerate AP for
 * @returns The new action points value
 * @throws Error if party not found or database error
 */
export async function regenerateActionPoints(partyId: string): Promise<number> {
  // Fetch party money
  const { data: party, error: partyError } = await supabase
    .from('parties')
    .select('id, money')
    .eq('id', partyId)
    .single()

  if (partyError) {
    throw partyError
  }

  if (!party) {
    throw new Error(`Party not found: ${partyId}`)
  }

  // Fetch party district states for organization strength
  const { data: districtStates, error: districtError } = await supabase
    .from('party_district_states')
    .select('organization_strength')
    .eq('party_id', partyId)

  if (districtError) {
    throw districtError
  }

  // Calculate average organization strength
  const avgOrgStrength = calculateAverageOrganizationStrength(districtStates || [])

  // Calculate new AP total
  const newActionPoints = calculateActionPointsForTick(party.money, avgOrgStrength)

  // Update party's action_points
  const { data: updatedParty, error: updateError } = await supabase
    .from('parties')
    .update({ action_points: newActionPoints })
    .eq('id', partyId)
    .select('action_points')
    .single()

  if (updateError) {
    throw updateError
  }

  return updatedParty.action_points
}

/**
 * Regenerate action points for all active parties in a nation.
 * Used by tick processor to regenerate AP for all parties at once.
 *
 * @param nationId - The nation to process
 * @returns Array of { partyId, newActionPoints } for each party
 */
export async function regenerateActionPointsForNation(
  nationId: string
): Promise<{ partyId: string; newActionPoints: number }[]> {
  // Fetch all active parties in the nation
  const { data: parties, error: partiesError } = await supabase
    .from('parties')
    .select('id')
    .eq('nation_id', nationId)
    .eq('is_active', true)

  if (partiesError) {
    throw partiesError
  }

  if (!parties || parties.length === 0) {
    return []
  }

  // Regenerate AP for each party
  const results = await Promise.all(
    parties.map(async (party) => {
      const newActionPoints = await regenerateActionPoints(party.id)
      return { partyId: party.id, newActionPoints }
    })
  )

  return results
}

/**
 * Get current action points status for a party.
 * Fetches the party's stored action_points and queued actions to calculate available AP.
 *
 * @param partyId - The party to check
 * @param currentTick - The current game tick
 * @returns Object with total, spent, and available AP
 */
export async function fetchActionPointsStatus(
  partyId: string,
  currentTick: number
): Promise<{
  total: number
  spent: number
  available: number
}> {
  // Fetch party's current action_points
  const { data: party, error: partyError } = await supabase
    .from('parties')
    .select('action_points')
    .eq('id', partyId)
    .single()

  if (partyError) {
    throw partyError
  }

  // Fetch queued actions for current tick
  const { data: queuedActions, error: actionsError } = await supabase
    .from('party_actions')
    .select('action_points_spent')
    .eq('party_id', partyId)
    .eq('tick', currentTick)
    .eq('status', 'queued')

  if (actionsError) {
    throw actionsError
  }

  const total = party.action_points
  const spent = (queuedActions || []).reduce(
    (sum, action) => sum + action.action_points_spent,
    0
  )
  const available = Math.max(0, total - spent)

  return { total, spent, available }
}
